import { useMemo } from 'react'

import { useAccountingTimezone } from '~/hooks/useAccountingTimezone'
import { formatDate } from '~/utils/display'
import {
  AccountingTz,
  addDays,
  addMonths,
  addYears,
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from '~/utils/timezone'

export type Period = {
  label: string
  startDate?: Date
  endDate?: Date
}

export const DEFAULT_PERIOD: Period = {
  label: 'All time',
  startDate: undefined,
  endDate: undefined,
}

export const getPeriodLabel = (period: Omit<Period, 'label'>) => {
  if (period.startDate && period.endDate) {
    return `${formatDate(period.startDate, { short: true })} to ${formatDate(period.endDate, { short: true })}`
  }
  if (period.startDate) {
    return `From ${formatDate(period.startDate, { short: true })}`
  }
  if (period.endDate) {
    return `Until ${formatDate(period.endDate, { short: true })}`
  }
  return DEFAULT_PERIOD.label
}

export const getPeriodPresets = (tz: AccountingTz): Period[][] => {
  const now = new Date()
  const todayEnd = endOfDay(now, tz)
  const lastWeek = addDays(now, -7, tz)
  const lastMonth = addMonths(now, -1, tz)
  const lastYear = addYears(now, -1, tz)
  return [
    [DEFAULT_PERIOD],
    [
      {
        label: 'This week',
        startDate: startOfWeek(now, tz),
        endDate: todayEnd,
      },
      {
        label: 'This month',
        startDate: startOfMonth(now, tz),
        endDate: todayEnd,
      },
      {
        label: 'This year',
        startDate: startOfYear(now, tz),
        endDate: todayEnd,
      },
    ],
    [
      {
        label: 'Last week',
        startDate: startOfWeek(lastWeek, tz),
        endDate: endOfWeek(lastWeek, tz),
      },
      {
        label: 'Last month',
        startDate: startOfMonth(lastMonth, tz),
        endDate: endOfMonth(lastMonth, tz),
      },
      {
        label: 'Last year',
        startDate: startOfYear(lastYear, tz),
        endDate: endOfYear(lastYear, tz),
      },
    ],
    [
      {
        label: 'Last 30 days',
        startDate: startOfDay(addDays(now, -30, tz), tz),
        endDate: todayEnd,
      },
      {
        label: 'Last 90 days',
        startDate: startOfDay(addDays(now, -90, tz), tz),
        endDate: todayEnd,
      },
      {
        label: 'Last 6 months',
        startDate: startOfDay(addMonths(now, -6, tz), tz),
        endDate: todayEnd,
      },
    ],
  ] satisfies Period[][]
}

export const usePeriodPresets = (tzOverride?: AccountingTz) => {
  const [committedTz] = useAccountingTimezone()
  const tz = tzOverride ?? committedTz
  return useMemo(() => getPeriodPresets(tz), [tz])
}
